import moment from "moment";

/**
 * Format a date string to a readable format using Moment.js
 * @param {string|Date} dateInput - The date string or Date object to format
 * @param {string} format - The desired format (default: "D MMM YYYY")
 * @return {string} The formatted date string
 */
export function formatDate(dateInput, format = "D MMM YYYY") {
    if (!dateInput) return "";

    const date = moment(dateInput);
    if (!date.isValid()) return String(dateInput);

    return date.format(format);
}

/**
 * Format a UNIX timestamp to a readable date string
 * @param {number} timestamp - The UNIX timestamp in seconds
 * @param {string} format - The desired moment.js format (default: 'DD MMM YYYY, HH:mm')
 * @return {string} The formatted date string
 */
export function formatDateTimestamp(timestamp, format = "DD MMM YYYY, HH:mm") {
    if (!timestamp) return "";

    const date = moment.unix(timestamp);
    if (!date.isValid()) return String(timestamp);

    return date.format(format);
}

/**
 * Format a number to include commas
 * @param {number} number - The number to format
 * @return {string} The formatted number
 */
export function formatNumber(number) {
    if (number === null || number === undefined) return '';
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Format a currency value
 * @param {number} value - The value to format
 * @param {string} currencySymbol - The currency symbol to use (default: $)
 * @return {string} The formatted currency string
 */
export function formatCurrency(value, currencySymbol = '$') {
    if (value === null || value === undefined) return '';
    return `${currencySymbol}${formatNumber(parseFloat(value).toFixed(2))}`;
}

/**
 * Format a JSON-encoded consonant string into a readable space-separated format.
 * 
 * @param {string} consonant - A stringified JSON array of consonants (e.g., '["p", "b", "t"]')
 * @returns {string} - A space-separated string (e.g., 'p b t'), or the original string if parsing fails
 */
export function formatConsonants(consonant) {
    try {
        const arr = JSON.parse(consonant);
        // If parsed value is an array, join with space separator
        return Array.isArray(arr) ? arr.join(' ') : consonant;
    } catch {
        // If input is not valid JSON, return it as-is
        return consonant;
    }
}

/**
 * Format a JSON-encoded consonant string into a readable space-separated format.
 * 
 * @param {string} consonant - A stringified JSON array of consonants (e.g., '["p", "b", "t"]')
 * @returns {string} - A space-separated string (e.g., 'p b t'), or the original string if parsing fails
 */
export function formatDuration(duration) {
    if (!duration || duration <= 0) return '-';
    const minutes = Math.floor((duration / 60) % 60);
    const seconds = Math.floor(duration % 60);
    let result = '';

    if (minutes > 0) {
        result += `${minutes} mins`;
    }

    if (seconds > 0) {
        if (result) result += ', ';
        result += `${seconds} secs`;
    }

    return result || '0 secs';

}

/**
 * Formats a string by converting letter-number patterns like "C1V1" 
 * into HTML with superscript numbers, e.g., "C<sup>1</sup>V<sup>1</sup>".
 * Useful for phonetic or linguistic representations.
 * 
 * @param {string} str - The input string containing letter-number patterns.
 * @returns {string} - The formatted HTML string with superscripts.
 */
export function formatSuperscript(str) {
    if (!str) return '';
    return str.replace(/([A-Z])(\d+)/g, '$1<sub>$2</sub>');
}

// All professional roles as a structured array
export const roleGroups = [
    {
        label: 'Board Certified Professionals',
        roles: [
            { value: 'BCBA', name: 'BCBA (Board Certified Behavior Analyst)' },
            { value: 'BCaBA', name: 'BCaBA (Board Certified Assistant Behavior Analyst)' },
            { value: 'BCBA-D', name: 'BCBA-D (Doctoral-level BCBA)' }
        ]
    },
    {
        label: 'Behavior Technicians',
        roles: [
            { value: 'RBT', name: 'RBT (Registered Behavior Technician)' },
            { value: 'BT', name: 'Behavior Technician (Non-RBT)' }
        ]
    },
    {
        label: 'Speech & Language',
        roles: [
            { value: 'SLP', name: 'SLP (Speech-Language Pathologist)' },
            { value: 'SLPA', name: 'SLPA (Speech-Language Pathology Assistant)' }
        ]
    },
    {
        label: 'Occupational Therapy',
        roles: [
            { value: 'OT', name: 'OT (Occupational Therapist)' },
            { value: 'OTA', name: 'OTA (Occupational Therapy Assistant)' }
        ]
    },
    {
        label: 'Psychology',
        roles: [
            { value: 'Psychologist', name: 'Psychologist' },
            { value: 'School Psychologist', name: 'School Psychologist' }
        ]
    },
    {
        label: 'Education',
        roles: [
            { value: 'Special Education Teacher', name: 'Special Education Teacher' },
            { value: 'General Education Teacher', name: 'General Education Teacher' },
            { value: 'Paraprofessional', name: 'Paraprofessional/Teaching Assistant' }
        ]
    },
    {
        label: 'Clinical/Medical',
        roles: [
            { value: 'PT', name: 'PT (Physical Therapist)' },
            { value: 'Social Worker', name: 'Social Worker' },
            { value: 'Nurse', name: 'Nurse' },
            { value: 'Physician', name: 'Physician' }
        ]
    },
    {
        label: 'Students & Trainees',
        roles: [
            { value: 'Graduate Student', name: 'Graduate Student (ABA/Psychology)' },
            { value: 'Undergraduate Student', name: 'Undergraduate Student' },
            { value: 'BCBA Candidate', name: 'BCBA Candidate (Accruing Hours)' }
        ]
    },
    {
        label: 'Administrative & Support',
        roles: [
            { value: 'Clinical Director', name: 'Clinical Director' },
            { value: 'Program Administrator', name: 'Program Administrator' },
            { value: 'Parent/Caregiver', name: 'Parent/Caregiver' },
            { value: 'Other', name: 'Other Professional' }
        ]
    }
];